import {
  createAutosave,
  createNetworkState,
  createServices,
  createSyncEngine,
  type Autosave,
  type AutosaveOptions,
  type ContentDraft,
  type NetworkSource,
  type NetworkState,
  type Services,
  type SyncEngine,
  type SyncEngineOptions,
  type SyncTransport,
  type Timers,
} from "@hq/client";
import type { NowContext } from "@hq/core";
import type { LocalStore } from "@hq/local-store";
import { createContext, type ReactNode, useContext } from "react";

export type ClientContextValue = {
  store: LocalStore;
  services: Services;
  sync: SyncEngine;
  autosave: Autosave<ContentDraft>;
  taskAutosave: Autosave<ContentDraft>;
  network: NetworkState;
};

export type CreateClientContextOptions = {
  store: LocalStore;
  transport: SyncTransport;
  /** Connectivity source; `browserNetworkSource()` in the web app. Defaults to always online. */
  network?: NetworkSource;
  clock?: () => NowContext;
  timers?: Timers;
  autosaveDebounceMs?: number;
  /** Keep mutations local without attempting delivery to an unconfigured server. */
  syncEnabled?: boolean | (() => boolean);
  sync?: Pick<SyncEngineOptions, "intervalMs" | "batchSize" | "pullLimit" | "kickDelayMs">;
};

/**
 * Wires store, services, sync engine and autosave together the way the app needs them:
 * services kick the engine after every local write, the engine shares its in-flight set with
 * services so content writes coalesce safely, and autosave persists through services.
 */
export function createClientContext(options: CreateClientContextOptions): ClientContextValue {
  const { store, transport } = options;
  const inFlight = new Set<string>();
  const network = createNetworkState(options.network);
  const enabled = () =>
    typeof options.syncEnabled === "function"
      ? options.syncEnabled()
      : options.syncEnabled !== false;
  const engineOptions: SyncEngineOptions = {
    store,
    enabled,
    transport,
    services: { inFlight },
    network,
    ...options.sync,
  };
  if (options.clock) engineOptions.clock = options.clock;
  if (options.timers) engineOptions.timers = options.timers;
  const sync = createSyncEngine(engineOptions);
  const serviceOptions: Parameters<typeof createServices>[1] = {
    inFlight,
    onMutation: () => {
      if (enabled()) sync.kick();
    },
  };
  if (options.clock) serviceOptions.clock = options.clock;
  const services = createServices(store, serviceOptions);
  const timing: Omit<AutosaveOptions<ContentDraft>, "save"> = {};
  if (options.autosaveDebounceMs !== undefined) timing.debounceMs = options.autosaveDebounceMs;
  if (options.timers) timing.timers = options.timers;
  const autosave = createAutosave<ContentDraft>({
    ...timing,
    save: (noteId, draft, baseRevision) =>
      (typeof draft === "string"
        ? services.notes.updateMarkdown(noteId, draft, baseRevision)
        : services.notes.updateContent(noteId, draft, baseRevision)
      ).then(() => undefined),
  });
  const taskAutosave = createAutosave<ContentDraft>({
    ...timing,
    save: (taskId, draft, baseRevision) =>
      (typeof draft === "string"
        ? services.tasks.setDescription(taskId, draft || null, baseRevision)
        : services.tasks.setDescriptionContent(taskId, draft, baseRevision)
      ).then(() => undefined),
  });
  return { store, services, sync, autosave, taskAutosave, network };
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined);

export function ClientProvider(props: { value: ClientContextValue; children?: ReactNode }) {
  return <ClientContext.Provider value={props.value}>{props.children}</ClientContext.Provider>;
}

export function useClient(): ClientContextValue {
  const value = useContext(ClientContext);
  if (!value) throw new Error("useClient must be used inside <ClientProvider>");
  return value;
}

export function useStore(): LocalStore {
  return useClient().store;
}

export function useServices(): Services {
  return useClient().services;
}

export function useSyncEngine(): SyncEngine {
  return useClient().sync;
}

export function useAutosave(): Autosave<ContentDraft> {
  return useClient().autosave;
}

export function useNetwork(): NetworkState {
  return useClient().network;
}
